import { Flex, Text, ScrollArea, Spinner } from "@radix-ui/themes";
import { ReactNode, useCallback, useEffect, useMemo, useRef } from "react";

import { Tabs, TabsList, TabsTrigger } from "@/v2/components/dataTabs/tabs";
import {
  ExploreFilterMenu,
  ExploreFiltersValue,
} from "@/v2/features/explore-filter-menu/ExploreFilterMenu";
import { FilterMenu } from "@/v2/components/filter-menu/FilterMenu";
import { FilterMenuType } from "@/v2/components/filter-menu/FilterMenu.model";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { ExploreInteractionType } from "@/v2/entities/explore/Explore.model";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { Comment01Icon } from "@/v2/assets/icons/Comment01Icon";
import { Ticket03Icon } from "@/v2/assets/icons/Ticket03Icon";

import styles from "../../../styles.module.scss";

import { InteractionListHeader } from "./InteractionListHeader";
import { useSimulator } from "../../../context/SimulatorWrapper";

interface InteractionListViewProps {
  showTabs?: boolean;
  selectedTabValue?: string;
  onTabChange?: (value: string) => void;
  tabOptions?: Array<{ value: string; label: string }>;

  activeFilters: ExploreFiltersValue;
  setActiveFilters: (filters: ExploreFiltersValue) => void;
  filterSource?: ExploreInteractionType;
  allowedFilterFields?: string[];
  hideFilterCount?: boolean;
  filter?: Filter;
  customFilterOptions?: Record<string, Array<{ label: string; value: string }>>;

  children: ReactNode;

  isLoading?: boolean;
  isEmpty?: boolean;
  loadingText?: string;
  emptyText?: string;

  customHeaderContent?: ReactNode;

  // Search functionality
  search?: string;
  onSearchChanged?: (search: string) => void;
  showSearchAndFilters?: boolean;

  // Pagination
  hasNextPage?: boolean;
  fetchNextPage?: () => Promise<void>;
  isFetchingNextPage?: boolean;
}

export const InteractionListView = ({
  showTabs = false,
  selectedTabValue,
  onTabChange,
  tabOptions = [
    { value: "tickets", label: "Tickets" },
    { value: "conversations", label: "Conversations" },
  ],
  activeFilters,
  setActiveFilters,
  filterSource,
  allowedFilterFields,
  hideFilterCount = true,
  filter,
  customFilterOptions,
  children,
  isLoading = false,
  isEmpty = false,
  loadingText = "Loading...",
  emptyText = "No items",
  customHeaderContent,
  search = "",
  onSearchChanged,
  showSearchAndFilters = false,
  hasNextPage = false,
  fetchNextPage,
  isFetchingNextPage = false,
}: InteractionListViewProps) => {
  const { topics } = useSimulator();
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || !fetchNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  const handleTabChange = useCallback(
    (value: string) => {
      onTabChange?.(value);
    },
    [onTabChange]
  );

  const sessionFilterMenu = useMemo((): FilterMenuType<string> => {
    const menu: FilterMenuType<string> = [];

    if (topics && topics.length > 0) {
      menu.push({
        field: "topic",
        kind: "multi",
        label: "Topic",
        options: topics.map((topic) => ({
          label: topic.topic,
          value: topic.topic,
        })),
      });
    }

    menu.push(
      {
        field: "sessionInteractionType",
        kind: "multi",
        label: "Interaction Type",
        options: [
          { label: "From Conversations", value: "conversations" },
          { label: "From Tickets", value: "tickets" },
          { label: "Pending Review", value: "pending_review" },
        ],
      },
      {
        field: "feedbackType",
        kind: "multi",
        label: "Feedback Type",
        options: [
          { label: "Positive", value: "POSITIVE" },
          { label: "Negative", value: "NEGATIVE" },
          { label: "None", value: "NONE" },
        ],
      }
    );

    if (customFilterOptions?.sessionVersion) {
      menu.push({
        field: "sessionVersion",
        kind: "multi",
        label: "Session Version",
        options: customFilterOptions.sessionVersion,
      });
    }

    if (customFilterOptions?.quackUserId) {
      menu.push({
        field: "quackUserId",
        kind: "multi",
        label: "Quack User ID",
        options: customFilterOptions.quackUserId,
      });
    }

    return menu;
  }, [customFilterOptions, topics]);

  const shouldUseSessionFilters = useMemo(() => {
    const hasSessionFields = allowedFilterFields?.some(
      (field) =>
        field.startsWith("session") ||
        field === "quackUserId" ||
        field === "feedbackType"
    );

    const hasSessionOptions =
      customFilterOptions?.sessionVersion || customFilterOptions?.quackUserId;

    return hasSessionFields || hasSessionOptions;
  }, [allowedFilterFields, customFilterOptions]);

  const renderLoadingState = () => (
    <Flex
      direction="column"
      align="center"
      justify="center"
      py="6"
      gap="2"
      style={{ minHeight: "200px" }}
    >
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      <Text size="2" color="gray">
        {loadingText}
      </Text>
    </Flex>
  );

  const renderEmptyState = () => (
    <Flex
      direction="column"
      align="center"
      justify="center"
      py="6"
      gap="2"
      style={{ minHeight: "200px" }}
    >
      <Text size="2" color="gray">
        {emptyText}
      </Text>
    </Flex>
  );

  return (
    <>
      <Flex
        gap="2"
        p="2"
        width="100%"
        direction="column"
        align="start"
        justify="start"
      >
        {showTabs ? (
          <Tabs
            className="w-full"
            value={selectedTabValue}
            onValueChange={handleTabChange}
          >
            <TabsList size="sm" fullWidth>
              {tabOptions.map((option) => (
                <TabsTrigger
                  key={option.value}
                  value={option.value}
                  size="sm"
                  fullWidth
                >
                  {option.value === "tickets" ? (
                    <Ticket03Icon fill="blue" />
                  ) : (
                    <Comment01Icon fill="orange" />
                  )}
                  {option.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        ) : customHeaderContent ? (
          customHeaderContent
        ) : null}

        {showSearchAndFilters && onSearchChanged ? (
          <InteractionListHeader
            search={search}
            onSearchChanged={onSearchChanged}
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
            filterSource={filterSource}
            allowedFilterFields={allowedFilterFields}
            filter={filter}
            hideFilterCount={hideFilterCount}
            customFilterOptions={customFilterOptions}
          />
        ) : shouldUseSessionFilters ? (
          <FilterMenu
            size="1"
            menu={sessionFilterMenu}
            value={activeFilters}
            onChange={setActiveFilters}
            label="Filter"
            hideCount={hideFilterCount}
          />
        ) : (
          <ExploreFilterMenu
            size="1"
            source={filterSource}
            value={activeFilters}
            onChange={setActiveFilters}
            hideCount={hideFilterCount}
            allowedFields={allowedFilterFields}
          />
        )}
      </Flex>

      <ScrollArea scrollbars="vertical" className={styles.ticketViewScrollArea}>
        <Flex direction="column" gap="1" p="2">
          {isLoading
            ? renderLoadingState()
            : isEmpty
            ? renderEmptyState()
            : children}
          {hasNextPage && (
            <Flex ref={sentinelRef} align="center" justify="center" py="4">
              {isFetchingNextPage && <Spinner size="2" />}
            </Flex>
          )}
        </Flex>
      </ScrollArea>
    </>
  );
};
